import React, { FC, useEffect, useMemo, useState } from "react";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import SearchInput from "../filterInput/SearchInput";
import ResourceTypeSelect from "../selects/ResourceTypeSelect";
import FloorSelect from "../selects/FloorSelect";
import BranchSelect from "../selects/BranchSelect";
import ExtraSelect from "../selects/ExtraSelect";
import { ResourceType } from "../../resource/resourceType.model";
import { Floor } from "../../floor/floor.model";
import { Branch } from "../../branches/branch.model";
import { BranchReference } from "../../branches/branch.reference.model";
import { Amenity } from "../../amenities/amenity.model";
import { Paginated } from "../Paginated";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { BranchInfo } from "../../../_services/branch.service";

interface ResourceFiltersBarProps {
  types: ResourceType[];
  type: ResourceType | null;
  floors: Floor[] | Paginated<Floor> | null;
  floor: Floor | undefined;
  favoriteFloorId?: string | null;
  branches: BranchReference[];
  branch: Branch | null;
  branchInfo: BranchInfo | null;
  extras: Amenity[];
  extra: Amenity | null;
  onNameSearch: (query: string) => void;
  onResourceTypeChange: (type: ResourceType) => void;
  onFloorChange: (floor: Floor) => void;
  onBranchChange: (branch: BranchReference) => void;
  onExtraChange: (extra: Amenity) => void;
  onFloorFavotired?: (floorId: string) => void;
  smallSize?: boolean;
  hideLabels?: boolean;
  showSearchIcon?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    padding: 2,
  },
}));

const ResourceFiltersBar: FC<ResourceFiltersBarProps> = ({
  types,
  type,
  floors,
  floor,
  favoriteFloorId,
  branches,
  branch,
  extras,

  extra,
  branchInfo,
  onNameSearch,
  onResourceTypeChange,
  onFloorChange,
  onBranchChange,
  onExtraChange,
  onFloorFavotired,
  smallSize = false,
  hideLabels = false,
  showSearchIcon = false,
}) => {
  const classes = useStyles();
  const [searching, setSearching] = useState(false);

  const handleFocus = () => {
    setSearching(true);
  };

  const handleBlur = () => {
    setSearching(false);
  };

  const filteredTypes = useMemo(() => {
    if (!branchInfo || !types) {
      return types;
    }

    const validTypeIds = new Set<string>();

    // Get the relevant floor(s) to check
    const floorsToCheck = floor
      ? [branchInfo[floor.id]]
      : Object.values(branchInfo);

    floorsToCheck.forEach((floorData) => {
      if (!floorData) return;

      floorData.resourceTypes?.forEach((rt) => {
        // If amenity filter is selected, check if this resource type has it
        if (extra) {
          const hasAmenity = rt.amenities?.some(
            (a) => a.amenityId === extra.id,
          );
          if (hasAmenity) {
            validTypeIds.add(rt.resourceTypeId);
          }
        } else {
          // No amenity filter, add all resource types
          validTypeIds.add(rt.resourceTypeId);
        }
      });
    });

    // Return only types that have valid resources
    return types.filter((t) => validTypeIds.has(t.id));
  }, [branchInfo, types, floor, extra]);

  const validFloorIds = useMemo(() => {
    if (!branchInfo || !floors) {
      const floorsArray = floors
        ? "docs" in floors
          ? floors.docs
          : floors
        : [];
      return new Set<string>(floorsArray.map((f) => f.id) || []);
    }

    const validIds = new Set<string>();

    Object.entries(branchInfo).forEach(([floorId, floorData]) => {
      if (!floorData) return;

      let hasValidResources = false;

      // Check if floor has resources matching current filters
      floorData.resourceTypes?.forEach((rt) => {
        // If resource type filter is selected, check if this floor has it
        if (type && rt.resourceTypeId !== type.id) {
          return;
        }

        // If amenity filter is selected, check if resources in this floor have it
        if (extra) {
          const hasAmenity = rt.amenities?.some(
            (a) => a.amenityId === extra.id,
          );
          if (hasAmenity) {
            hasValidResources = true;
          }
        } else {
          // No amenity filter, this floor is valid
          hasValidResources = true;
        }
      });

      if (hasValidResources) {
        validIds.add(floorId);
      }
    });

    return validIds;
  }, [branchInfo, floors, type, extra]);

  const validExtraIds = useMemo(() => {
    if (!branchInfo || !extras) {
      return new Set<string>(extras?.map((e) => e.id) || []);
    }

    const validAmenityIds = new Set<string>();

    // Get the relevant floor(s) to check
    const floorsToCheck = floor
      ? [branchInfo[floor.id]]
      : Object.values(branchInfo);

    floorsToCheck.forEach((floorData) => {
      if (!floorData) return;

      // If resource type is selected, only look at that resource type's amenities
      if (type) {
        const resourceType = floorData.resourceTypes?.find(
          (rt) => rt.resourceTypeId === type.id,
        );
        if (resourceType) {
          resourceType.amenities?.forEach((amenity) => {
            validAmenityIds.add(amenity.amenityId);
          });
        }
      } else {
        // No resource type filter, show all amenities available in the floor
        floorData.amenities?.forEach((amenity) => {
          validAmenityIds.add(amenity.amenityId);
        });
      }
    });

    return validAmenityIds;
  }, [branchInfo, extras, floor, type]);

  // Reset resource type if it's no longer valid after filters change
  useEffect(() => {
    if (type && !filteredTypes.some((t) => t.id === type.id)) {
      onResourceTypeChange(null);
    }
  }, [filteredTypes, type, onResourceTypeChange]);

  // Normalize floors to always be an array
  const floorsArray = floors ? ("docs" in floors ? floors.docs : floors) : [];

  return (
    <Grid container alignItems="center">
      <Grid item>
        {showSearchIcon && !searching ? (
          <Tooltip title="Buscar">
            <IconButton
              className={classes.icon}
              aria-label="search"
              onClick={handleFocus}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <SearchInput
            value={""}
            onSearch={onNameSearch}
            onBlur={showSearchIcon ? handleBlur : undefined}
            placeholder="Buscar recursos"
          />
        )}
      </Grid>
      <Grid item>
        <ResourceTypeSelect
          smallSize={smallSize}
          hideLabel={hideLabels}
          types={filteredTypes}
          defaultValue={type}
          onChange={onResourceTypeChange}
        />
      </Grid>
      <Grid item>
        <FloorSelect
          smallSize={smallSize}
          hideLabel={hideLabels}
          floors={floorsArray}
          validFloorIds={validFloorIds}
          defaultValue={floor}
          favouriteFloorId={favoriteFloorId}
          onChange={onFloorChange}
          onFavourite={onFloorFavotired}
        />
      </Grid>
      <Grid item>
        <BranchSelect
          smallSize={smallSize}
          hideLabel={hideLabels}
          branches={branches}
          defaultValue={branch}
          onChange={onBranchChange}
        />
      </Grid>
      {extras && extras.length > 0 && (
        <Grid item>
          <ExtraSelect
            smallSize={smallSize}
            hideLabel={hideLabels}
            extras={extras}
            validExtraIds={validExtraIds}
            defaultValue={extra}
            showAll={true}
            disabled={!extras || extras.length === 0}
            onChange={onExtraChange}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ResourceFiltersBar;
